import { useEffect, useRef, useState } from 'react';

import { BrandBar } from '../components/index.js';
import { LegalFooter } from '../components/LegalFooter.js';
import { usePrefersReducedMotion } from '../hooks.js';
import { CATEGORIES, OFFERING, QUESTIONS, SECONDS_PER_QUESTION } from '@shared/config.js';
import type { OptionIndex } from '@shared/types.js';

/** Duration of the confirm sweep before auto-advance. Mirrors --dur-confirm. */
const CONFIRM_MS = 1400;

interface Props {
  index: number;
  answers: (OptionIndex | null)[];
  onAnswer: (index: number, option: OptionIndex) => void;
  onAdvance: () => void;
  onBack: () => void;
}

export default function QuestionScreen({ index, answers, onAnswer, onAdvance, onBack }: Props) {
  const reducedMotion = usePrefersReducedMotion();
  const question = QUESTIONS[index];
  const category = CATEGORIES.find((c) => c.id === question.categoryId)!;

  /** The option currently running its confirm sweep, if any. */
  const [confirming, setConfirming] = useState<OptionIndex | null>(null);
  /** Increments on every selection so the sweep animation restarts. */
  const [confirmSeq, setConfirmSeq] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const headingRef = useRef<HTMLParagraphElement>(null);

  const selected = confirming ?? answers[index];
  const remainingMin = Math.max(1, Math.round(((QUESTIONS.length - index) * SECONDS_PER_QUESTION) / 60));
  const toGo = QUESTIONS.length - index - 1;

  // Cancel any in-flight advance when the question changes or we unmount, so
  // Back never lands the visitor on the wrong screen a beat later.
  useEffect(() => {
    setConfirming(null);
    headingRef.current?.focus();
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [index]);

  function select(option: OptionIndex) {
    // The confirm sweep is a deliberate pause, NOT a lock. A visitor who
    // mis-taps must be able to pick a different option: cancel the pending
    // advance and restart with the new choice. (Under reduced motion there is
    // no timer at all, so without this the answer could never be changed.)
    if (timer.current) clearTimeout(timer.current);

    onAnswer(index, option);
    setConfirming(option);
    // Bump the key so the sweep animation restarts even when the same option
    // is re-selected — otherwise the bar would stay where it was.
    setConfirmSeq((seq) => seq + 1);

    if (!reducedMotion) {
      timer.current = setTimeout(onAdvance, CONFIRM_MS);
    }
  }

  function back() {
    if (timer.current) clearTimeout(timer.current);
    setConfirming(null);
    onBack();
  }

  return (
    <div className="app app--fixed">
      <BrandBar tag={OFFERING.name} />

      <main className="screen">
        <div className="q-col">
          <div className="progress" style={{ marginBottom: 18 }}>
            <div className="prog-top">
              <span className="prog-q">
                Question {index + 1} of {QUESTIONS.length}
              </span>
              <span className="prog-time">
                ~{remainingMin} min left
                {toGo > 0 && <span className="q-togo"> · {toGo} to go</span>}
              </span>
            </div>
            <div
              className="prog-track"
              role="progressbar"
              aria-valuemin={1}
              aria-valuemax={QUESTIONS.length}
              aria-valuenow={index + 1}
              aria-label={`Question ${index + 1} of ${QUESTIONS.length}`}
            >
              {QUESTIONS.map((q, i) => (
                <span
                  key={q.id}
                  className={`prog-seg ${i < index ? 'done' : i === index ? 'cur' : 'pending'}`}
                />
              ))}
            </div>
          </div>

          <span className="kicker">{category.name}</span>
          <p className="q-text" id="question-text" tabIndex={-1} ref={headingRef}>
            {question.text}
          </p>

          <div className="opt-list" role="group" aria-labelledby="question-text">
            {question.options.map((option, i) => {
              const optionIndex = i as OptionIndex;
              const isSelected = selected === optionIndex;
              return (
                <button
                  type="button"
                  // Remount the selected option on each pick so its sweep
                  // animation replays from zero.
                  key={isSelected ? `${option.label}-${confirmSeq}` : option.label}
                  className={`opt${isSelected ? ' sel' : ''}`}
                  aria-pressed={isSelected}
                  onClick={() => select(optionIndex)}
                >
                  <span className="opt-mark" aria-hidden="true" />
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>

          <div className="q-foot">
            <button type="button" className="btn-back" onClick={back} disabled={index === 0}>
              <span className="back-arrow" aria-hidden="true" />
              Back
            </button>

            {/* Reduced motion gets an explicit control instead of a timer. */}
            {reducedMotion
              ? confirming !== null && (
                  <button type="button" className="btn btn-primary" onClick={onAdvance}>
                    Continue
                    <span className="btn-arrow" aria-hidden="true" />
                  </button>
                )
              : confirming !== null && <span className="confirming">Confirming…</span>}
          </div>

          <p aria-live="polite" className="sr-only">
            {confirming !== null
              ? `Answer recorded. You can still choose a different option. ${
                  reducedMotion
                    ? 'Select continue to go to the next question.'
                    : 'Otherwise, moving to the next question.'
                }`
              : ''}
          </p>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
